import type { CompanyWatermarkSettings } from './types';

import { ENDPOINT } from '~/constants/endpoints';

import { type UUID } from '~/types/common';

import { HTTPError, vestigasApi } from '~/services/kyClient';
import ToastService from '~/services/toast.service';

import { i18n, log } from '~/utils';

import { zodCompanyWatermarkSettingsSchema } from './validations';

/**
 * Fetches company watermark settings from the API.
 * @param companyId - The ID of the company to fetch watermark settings for.
 * @returns The company watermark settings.
 * @throws Error if the API request fails.
 * @see https://app.dev.vestigas.com/redoc#tag/VESTIGAS-Admin-Company/operation/get_company_watermark_settings_vadmin_company__company_id__watermark_settings_get
 */
export const fetchCompanyWatermarkSettings = async (companyId: UUID) => {
  try {
    return await vestigasApi
      .get(ENDPOINT.COMPANY.WATERMARK_SETTINGS.GET(companyId), {
        validate: { response: zodCompanyWatermarkSettingsSchema },
      })
      .json<CompanyWatermarkSettings>();
  } catch (error) {
    log.error({
      message: 'Error fetching company watermark settings',
      error,
    });

    if (error instanceof HTTPError) {
      let errorMessage = '';

      switch (error.response.status) {
        case 404: {
          errorMessage = i18n.t(
            ($) => $.company.fetchCompanyWatermarkSettings.error404,
            {
              ns: 'data',
              companyId,
            },
          );
          break;
        }

        default: {
          errorMessage = i18n.t(
            ($) => $.company.fetchCompanyWatermarkSettings.error.generic,
            { ns: 'data' },
          );
        }
      }

      ToastService.error(errorMessage);
    }

    throw error;
  }
};
